import { useInboxMailHorizonQuery } from "@qw/graphql";
import { format } from "date-fns";

/**
 * How far back QWIB's mail reaches, phrased for a broker whose search just came back empty.
 *
 * For the median inbox that is about eleven months, so a real share of "I couldn't find it in
 * QWIB" is mail ingest never reached rather than mail that ranked badly. Today the two failures
 * look identical — an empty list — and this is the line that tells them apart.
 *
 * `Inbox.earliestMessageAt` is a `@ResolveField` over an aggregate, deliberately kept out of the
 * `Inbox.gql` every app load already selects (B-1752). `skip` is what preserves that: pass it true
 * until a search has actually found nothing, and the aggregate is never paid on a load that would
 * not have shown this.
 *
 * Null means "say nothing new" — the query is skipped or still in flight, or the inbox holds no
 * mail at all and there is no horizon to name.
 */
export const useMailHorizonDescription = ({ skip }: { skip: boolean }): string | null => {
  const { data } = useInboxMailHorizonQuery({ skip, fetchPolicy: "cache-first" });
  // The `DateTime` scalar is untyped in codegen; it arrives as an ISO string.
  const earliestMessageAt: string | null = data?.inbox?.earliestMessageAt ?? null;

  if (!earliestMessageAt) {
    return null;
  }

  // Named by month, which is as precise as the claim can honestly be. `earliestMessageAt` is the
  // last message of the oldest thread, so mail reaches at least that far back and usually further;
  // naming the month it falls in only ever understates what QWIB holds.
  return `QWIB has your mail from ${format(new Date(earliestMessageAt), "MMMM yyyy")}.`;
};
